import React from "react";
import { useNavigate } from "react-router-dom";

/**
 * Step 7: Success
 * Shows migration completion summary and next steps
 */
function Step7Success({ migrationData }) {
  const navigate = useNavigate();
  const results = migrationData?.importResults || {};

  // Helper to format scheduled date correctly (avoid timezone issues)
  // When parsing "2026-02-05" as a Date, JS interprets it as UTC midnight,
  // which can display as the previous day in local timezones
  const formatScheduledDate = (dateString) => {
    if (!dateString) return "";
    // Parse YYYY-MM-DD format directly to avoid timezone shifts
    const parts = dateString.split("T")[0].split("-");
    if (parts.length === 3) {
      const [year, month, day] = parts;
      // Create date at noon local time to avoid any date shift
      const date = new Date(
        parseInt(year),
        parseInt(month) - 1,
        parseInt(day),
        12,
        0,
        0,
      );
      return date.toLocaleDateString();
    }
    // Fallback for other formats
    return new Date(dateString).toLocaleDateString();
  };

  // Calculate totals
  const totalImported = Object.values(results).reduce(
    (sum, stat) => sum + (stat.imported || 0),
    0,
  );
  const totalSkipped = Object.values(results).reduce(
    (sum, stat) => sum + (stat.skipped || 0),
    0,
  );
  const totalErrors = Object.values(results).reduce(
    (sum, stat) => sum + (stat.errors || 0),
    0,
  );

  // Helper to render detailed stats for each data type
  const renderDataTypeStats = (dataType, icon, label) => {
    const stats = results[dataType];
    if (
      !stats ||
      (stats.imported === 0 && stats.skipped === 0 && stats.errors === 0)
    ) {
      return null;
    }
    return (
      <div key={dataType} style={styles.detailRow}>
        <div style={styles.detailIcon}>{icon}</div>
        <div style={styles.detailLabel}>{label}</div>
        <div style={styles.detailStats}>
          <span style={styles.importedBadge}>✓ {stats.imported} imported</span>
          {stats.skipped > 0 && (
            <span style={styles.skippedBadge}>⊘ {stats.skipped} skipped</span>
          )}
          {stats.errors > 0 && (
            <span style={styles.errorBadge}>✕ {stats.errors} errors</span>
          )}
        </div>
      </div>
    );
  };

  return (
    <div style={styles.container}>
      <div style={styles.successIcon}>🎉</div>
      <h2 style={styles.heading}>Migration Complete!</h2>
      <p style={styles.text}>
        Your data has been successfully imported from{" "}
        {migrationData?.sourcePlatform || "your previous platform"} to Ogestyle.
      </p>

      {/* Import Summary */}
      <div style={styles.summaryCard}>
        <h3 style={styles.summaryTitle}>Import Summary</h3>

        {/* Overview Stats */}
        <div style={styles.statsGrid}>
          <div style={styles.statBox}>
            <div style={styles.statNumber}>{totalImported}</div>
            <div style={styles.statLabel}>Records Imported</div>
          </div>
          {totalSkipped > 0 && (
            <div style={{ ...styles.statBox, borderColor: "#ffc107" }}>
              <div style={{ ...styles.statNumber, color: "#f59e0b" }}>
                {totalSkipped}
              </div>
              <div style={styles.statLabel}>Skipped (duplicates)</div>
            </div>
          )}
          {totalErrors > 0 && (
            <div style={{ ...styles.statBox, borderColor: "#ef5350" }}>
              <div style={{ ...styles.statNumber, color: "#ef5350" }}>
                {totalErrors}
              </div>
              <div style={styles.statLabel}>Errors</div>
            </div>
          )}
        </div>

        {/* Detailed Breakdown by Type */}
        <div style={styles.detailsSection}>
          <h4 style={styles.detailsTitle}>Breakdown by Type</h4>
          {renderDataTypeStats("services", "✂️", "Services")}
          {renderDataTypeStats("customers", "👥", "Customers")}
          {renderDataTypeStats("appointments", "📅", "Appointments")}
          {renderDataTypeStats("staff", "👤", "Staff")}
        </div>

        {totalSkipped > 0 && (
          <div style={styles.skippedNotice}>
            ℹ️ Skipped records were duplicates already in your system.
          </div>
        )}
        {totalErrors > 0 && (
          <div style={styles.errorNotice}>
            ⚠️ {totalErrors} records had errors and were not imported. Check
            your email for details.
          </div>
        )}
      </div>

      {/* Next Steps */}
      <div style={styles.nextSteps}>
        <h3 style={styles.nextStepsTitle}>What's Next?</h3>
        <div style={styles.stepsList}>
          <div style={styles.step}>
            <div style={styles.stepIcon}>✓</div>
            <div>
              <div style={styles.stepTitle}>Review Your Data</div>
              <div style={styles.stepDesc}>
                Check imported services, customers, and appointments
              </div>
            </div>
          </div>
          <div style={styles.step}>
            <div style={styles.stepIcon}>✓</div>
            <div>
              <div style={styles.stepTitle}>Configure Settings</div>
              <div style={styles.stepDesc}>
                Set up working hours, booking policies, and notifications
              </div>
            </div>
          </div>
          <div style={styles.step}>
            <div style={styles.stepIcon}>✓</div>
            <div>
              <div style={styles.stepTitle}>Go Live!</div>
              <div style={styles.stepDesc}>
                {migrationData?.goLiveSettings?.scheduledDate
                  ? `Your business will go live on ${formatScheduledDate(migrationData?.goLiveSettings?.scheduledDate)}`
                  : "Use your dashboard Go Live panel when you're ready for your ribbon-cutting go-live."}
              </div>
            </div>
          </div>
        </div>
      </div>

      {migrationData?.goLiveSettings?.notifyCustomers && (
        <div style={styles.notificationBox}>
          📧 Customer notification emails will be sent when your business goes
          live.
        </div>
      )}

      <div style={styles.actions}>
        <button
          onClick={() => {
            // Navigate based on business type - freelancers have their own dashboard
            const dashboardPath =
              migrationData?.type === "freelancer"
                ? "/dashboard/freelancer"
                : "/dashboard";
            navigate(dashboardPath);
          }}
          style={styles.button}
        >
          Go to Dashboard
        </button>
      </div>
    </div>
  );
}

const styles = {
  container: {
    backgroundColor: "#fff",
    borderRadius: "12px",
    padding: "48px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    textAlign: "center",
    maxWidth: "800px",
    margin: "0 auto",
  },
  successIcon: { fontSize: "64px", marginBottom: "24px" },
  heading: {
    fontSize: "32px",
    fontWeight: 700,
    color: "#333",
    marginBottom: "16px",
  },
  text: {
    fontSize: "18px",
    color: "#666",
    marginBottom: "32px",
    lineHeight: "1.5",
  },
  summaryCard: {
    backgroundColor: "#f9f9f9",
    borderRadius: "12px",
    padding: "24px",
    marginBottom: "32px",
    textAlign: "left",
  },
  summaryTitle: {
    fontSize: "18px",
    fontWeight: 600,
    color: "#333",
    marginBottom: "20px",
    textAlign: "center",
  },
  statsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
    gap: "16px",
    marginBottom: "16px",
  },
  statBox: {
    textAlign: "center",
    padding: "16px",
    backgroundColor: "#fff",
    borderRadius: "8px",
    border: "1px solid #e0e0e0",
  },
  statNumber: {
    fontSize: "32px",
    fontWeight: 700,
    color: "#4CAF50",
    marginBottom: "8px",
  },
  statLabel: {
    fontSize: "14px",
    color: "#666",
  },
  errorNotice: {
    backgroundColor: "#fff3cd",
    border: "1px solid #ffc107",
    borderRadius: "8px",
    padding: "12px",
    fontSize: "14px",
    color: "#856404",
    textAlign: "center",
  },
  nextSteps: {
    textAlign: "left",
    marginBottom: "32px",
  },
  nextStepsTitle: {
    fontSize: "18px",
    fontWeight: 600,
    color: "#333",
    marginBottom: "16px",
  },
  stepsList: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  step: {
    display: "flex",
    gap: "16px",
    padding: "16px",
    backgroundColor: "#f9f9f9",
    borderRadius: "8px",
  },
  stepIcon: {
    width: "32px",
    height: "32px",
    borderRadius: "50%",
    backgroundColor: "#4CAF50",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: "bold",
    flexShrink: 0,
  },
  stepTitle: {
    fontSize: "16px",
    fontWeight: 600,
    color: "#333",
    marginBottom: "4px",
  },
  stepDesc: {
    fontSize: "14px",
    color: "#666",
    lineHeight: "1.5",
  },
  notificationBox: {
    backgroundColor: "#e3f2fd",
    border: "1px solid #2196F3",
    borderRadius: "8px",
    padding: "16px",
    fontSize: "14px",
    color: "#1565c0",
    marginBottom: "32px",
  },
  actions: {
    display: "flex",
    justifyContent: "center",
  },
  button: {
    padding: "14px 32px",
    backgroundColor: "#4CAF50",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    fontSize: "16px",
    fontWeight: 600,
    cursor: "pointer",
    transition: "background-color 0.2s",
  },
  // New styles for detailed breakdown
  detailsSection: {
    marginTop: "24px",
    paddingTop: "16px",
    borderTop: "1px solid #e0e0e0",
  },
  detailsTitle: {
    fontSize: "14px",
    fontWeight: 600,
    color: "#666",
    marginBottom: "12px",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  detailRow: {
    display: "flex",
    alignItems: "center",
    padding: "12px",
    backgroundColor: "#fff",
    borderRadius: "8px",
    marginBottom: "8px",
    border: "1px solid #e0e0e0",
  },
  detailIcon: {
    fontSize: "20px",
    marginRight: "12px",
  },
  detailLabel: {
    flex: 1,
    fontSize: "15px",
    fontWeight: 500,
    color: "#333",
  },
  detailStats: {
    display: "flex",
    gap: "8px",
    flexWrap: "wrap",
  },
  importedBadge: {
    backgroundColor: "#d4edda",
    color: "#155724",
    padding: "4px 10px",
    borderRadius: "12px",
    fontSize: "13px",
    fontWeight: 500,
  },
  skippedBadge: {
    backgroundColor: "#fff3cd",
    color: "#856404",
    padding: "4px 10px",
    borderRadius: "12px",
    fontSize: "13px",
    fontWeight: 500,
  },
  errorBadge: {
    backgroundColor: "#f8d7da",
    color: "#721c24",
    padding: "4px 10px",
    borderRadius: "12px",
    fontSize: "13px",
    fontWeight: 500,
  },
  skippedNotice: {
    backgroundColor: "#e3f2fd",
    border: "1px solid #2196F3",
    borderRadius: "8px",
    padding: "12px",
    fontSize: "14px",
    color: "#1565c0",
    textAlign: "center",
    marginTop: "16px",
  },
};

export default Step7Success;
